import React, { useState } from "react";
import { Flex, Text, Link as Href, Image, Box } from "@theme-ui/components";
import { useSelector, useDispatch } from "react-redux";
import { navigate } from "gatsby";
import { components } from "react-select";
import newFeatureGIF from "../../images/newFeature-MenuItem.gif";
import { setPageRegion } from "@greythr/shared-store/reducers/app";
import { SUPPORTED_REGIONS } from "@greythr/shared-utils/region";
import { Dropdown, Icon } from "../atoms";
import { colors, typography } from "../../styles/theme";

const RegionSelector = () => {
  const dispatch = useDispatch();
  const options = SUPPORTED_REGIONS.reduce((acc, curr) => {
    acc.push({ label: curr.name, value: curr.code });
    return acc;
  }, []);
  const region = useSelector((state) => state.app.region);
  const [selectedRegion, setSelectedRegion] = useState(
    options.filter((r) => r.label === region || r.value === region),
  );

  const onChange = (option) => {
    setSelectedRegion(option);
    dispatch(setPageRegion(option.value));
    const selectedRegionObj = SUPPORTED_REGIONS.find(
      (r) => r.code === option.value,
    );
    const regionPath = selectedRegionObj?.path || "";
    if(!regionPath) {
      // IN (main site root): hard navigation so CloudFront serves main site index.html
      window.location.assign("/");
      return;
    }
    // Navigate to the region-specific path for other countries
    navigate(`/${regionPath}`);
  };

  const CustomSingleValue = (props) => {
    const { data } = props;

    return (
      <components.SingleValue {...props}>
        <Flex
          className="region-container"
          style={{
            alignItems: "center",
            gap: "0.375rem",
          }}
        >
          <Icon name="Globe" size={24} color={colors.neutral900} />
          <span>{data.value}</span>
        </Flex>
      </components.SingleValue>
    );
  };

  return (
    <Dropdown
      options={options}
      onChange={onChange}
      value={selectedRegion}
      menuPlacement="auto"
      aria-label="region-selector"
      customStyles={dropdownCustomStyles}
      components={{ SingleValue: CustomSingleValue }}
    />
  );
};

const AnnouncementBanner = ({
  newFeatureMenuItem,
  newFeatureMenuItemID,
  displayBannerSupportCTA = true,
  displayBannerLoginCTA = false,
  region,
}) => {
  const featureLink =
    newFeatureMenuItem?.length &&
    newFeatureMenuItem[0]?.url &&
    newFeatureMenuItem[0]?.label
      ? (() => {
          const url = newFeatureMenuItem[0]?.url;
          const internalDomains = ["https://www.greythr.com"];
          const isInternal =
            url.startsWith("/") ||
            internalDomains.some((domain) => url.startsWith(domain));

          return (
            <Href
              aria-label="Learn more about our new feature update"
              href={url}
              target={isInternal ? "_self" : "_blank"}
              rel={isInternal ? undefined : "noopener noreferrer"}
              id={newFeatureMenuItemID}
              sx={bannerTextStyles}
            >
              <Image src={newFeatureGIF} alt="new-feature-tag" sx={imgStyle} />
              <Text
                sx={{
                  ...typography.h6,
                  whiteSpace: "wrap",
                  color: colors.primaryMain,
                }}
              >
                {newFeatureMenuItem[0]?.label}
              </Text>
            </Href>
          );
        })()
      : null;

  return (
    <Box
      sx={{
        width: "100%",
        backgroundColor: colors.primary100,
        "@media (max-width: 1030px)": { display: "none" },
      }}
    >
      <Flex sx={WRAPPER_STYLE}>
        {featureLink}

        <Flex sx={bannerCTAContainer}>
          <Box>
            <RegionSelector />
          </Box>

          {displayBannerSupportCTA && region !== "ID" && region !== "ID-id" && (
            <Href
              aria-label="Visit our support page"
              id={`banner_support_${region.toLowerCase()}_link`}
              href="/greythr-help/"
              sx={linkStyles}
            >
              Support
            </Href>
          )}

          {displayBannerLoginCTA && (region === "IN" || region === "ME") && (
            <Href
              aria-label="Log in to your account"
              id={`banner_login_${region.toLowerCase()}_link`}
              href="login/"
              sx={linkStyles}
            >
              Login
            </Href>
          )}
        </Flex>
      </Flex>
    </Box>
  );
};

const WRAPPER_STYLE = {
  position: "relative",
  zIndex: 1031,
  py: "0.5625rem",
  px: "1.5rem",
  "@media (min-width: 1157px)": {
    px: "2rem",
  },
  maxWidth: "97.5rem",
  margin: "auto",
  boxSizing: "content-box",
  justifyContent: "flex-end",
};

const {
  imgStyle,
  linkStyles,
  bannerCTAContainer,
  dropdownCustomStyles,
  bannerTextStyles,
} = {
  imgStyle: {
    maxWidth: "3.75rem",
    height: "1.52rem",
    objectFit: "contain",
    mr: "0.25rem",
    flexShrink: 0,
  },
  linkStyles: {
    ...typography.h6,
    textDecoration: "none",
    color: colors.neutral900,
    "&:hover": {
      textDecoration: "none",
      color: colors.primaryMain,
    },
  },
  bannerCTAContainer: {
    gap: "1.5rem",
    justifyContent: "center",
    alignItems: "center",
    ...typography.h6,
    flexShrink: 0,
  },
  bannerTextStyles: {
    ...typography.h6,
    position: "absolute",
    left: "50%",
    top: "50%",
    transform: "translate(-50%, -50%)",
    display: "flex",
    alignItems: "center",
    textDecoration: "none",
    "&:hover": {
      color: colors.primaryMain,
      textDecoration: "none",
    },
  },
  dropdownCustomStyles: {
    control: {
      backgroundColor: "transparent",
      border: "none",
      padding: 0,
      height: "100%",
      hover: {
        ".region-container": {
          color: colors.primaryMain,
        },
        svg: {
          stroke: colors.primaryMain,
        },
      },
    },
    menu: {
      width: "17rem",
      marginLeft: "-13rem",
      zIndex: 9999,
      borderRadius: "0.438rem",
    },
    menuList: {
      width: "17rem",
      paddingLeft: "1.5rem",
      paddingRight: "1.5rem",
      paddingTop: "1rem",
      paddingBottom: "1rem",
      maxHeight: "none",
      overflowY: "visible",
    },
    indicatorsContainer: { display: "flex", alignItems: "center" },
    valueContainer: { display: "flex", alignItems: "center" },
    singleValue: {
      ...typography.h6,
      color: colors.neutral900,
      margin: 0,
    },
    option: { borderBottom: 0 },
    dropdownIndicator: {
      width: "100%",
    },
  },
};

export default AnnouncementBanner;
